import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Vault as VaultIcon, ArrowDownCircle, ArrowUpCircle, X, Coins, DollarSign, Euro, Banknote } from "lucide-react";
import QuickCustomerAdd from "@/components/QuickCustomerAdd";

const fmt = (n, d = 4) =>
  Number(n ?? 0).toLocaleString("tr-TR", { minimumFractionDigits: d, maximumFractionDigits: d });

// Fallback unit dictionary — replaced by backend response.
const DEFAULT_UNITS = {
  gold_pure_gr: { label: "Has Altın (gr)", kind: "gold_gram", fmt: 4 },
  gold_22: { label: "22 Ayar (gr)", kind: "gold_gram", purity: 0.916, fmt: 4 },
  gold_18: { label: "18 Ayar (gr)", kind: "gold_gram", purity: 0.75, fmt: 4 },
  ceyrek: { label: "Çeyrek Altın", kind: "coin", fmt: 2 },
  yarim: { label: "Yarım Altın", kind: "coin", fmt: 2 },
  tam: { label: "Tam Altın", kind: "coin", fmt: 2 },
  cumhuriyet: { label: "Cumhuriyet Altını", kind: "coin", fmt: 2 },
  ata: { label: "Ata Altın", kind: "coin", fmt: 2 },
  usd: { label: "USD", kind: "fiat", fmt: 2 },
  eur: { label: "EUR", kind: "fiat", fmt: 2 },
  try: { label: "TL", kind: "fiat", fmt: 2 },
};

const UNIT_ICONS = {
  gold_pure_gr: <Coins size={12} />,
  gold_22: <Coins size={12} />,
  gold_18: <Coins size={12} />,
  ceyrek: <Coins size={12} />,
  yarim: <Coins size={12} />,
  tam: <Coins size={12} />,
  cumhuriyet: <Coins size={12} />,
  ata: <Coins size={12} />,
  usd: <DollarSign size={12} />,
  eur: <Euro size={12} />,
  try: <Banknote size={12} />,
};

export default function Vault() {
  const [units, setUnits] = useState(DEFAULT_UNITS);
  const [accounts, setAccounts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [selected, setSelected] = useState(null);
  const [txnOpen, setTxnOpen] = useState(false);
  const [form, setForm] = useState({
    customer_id: "",
    type: "deposit",
    unit: "gold_pure_gr",
    quantity: "",
    purity: 0.995,
    note: "",
  });

  const load = async () => {
    try {
      const [u, a, c] = await Promise.all([
        api.get("/vault/units").catch(() => ({ data: { units: DEFAULT_UNITS } })),
        api.get("/vault/accounts"),
        api.get("/customers"),
      ]);
      setUnits(u.data.units || DEFAULT_UNITS);
      setAccounts(a.data);
      setCustomers(c.data);
    } catch (e) {
      toast.error("Emanet verisi alınamadı");
    }
  };
  useEffect(() => {
    load();
  }, []);

  const openDetail = async (cid) => {
    const r = await api.get(`/vault/${cid}`);
    setSelected(r.data);
    if (r.data.units) setUnits(r.data.units);
  };

  const openTxn = (type, cid = "", unit = "gold_pure_gr") => {
    setForm({ customer_id: cid, type, unit, quantity: "", purity: units[unit]?.purity || 0.995, note: "" });
    setTxnOpen(true);
  };

  const submit = async () => {
    if (!form.customer_id || !form.quantity) return toast.error("Müşteri ve miktar gerekli");
    try {
      const payload = {
        customer_id: form.customer_id,
        type: form.type,
        unit: form.unit,
        quantity: Number(form.quantity),
        purity: Number(form.purity),
        note: form.note,
      };
      await api.post("/vault/txn", payload);
      toast.success(`${units[form.unit]?.label || form.unit}: İşlem kaydedildi`);
      setTxnOpen(false);
      load();
      if (selected) openDetail(selected.customer.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Hata");
    }
  };

  const unitList = useMemo(() => Object.entries(units), [units]);
  const unitInfo = units[form.unit] || DEFAULT_UNITS.gold_pure_gr;
  const showPurity = unitInfo.kind === "gold_gram" && form.unit !== "gold_pure_gr";

  // Aggregate totals across all customers, per unit
  const totals = useMemo(() => {
    const t = {};
    for (const a of accounts) {
      const bal = a.balances || {};
      for (const [u, v] of Object.entries(bal)) {
        t[u] = round4((t[u] || 0) + Number(v || 0));
      }
    }
    return t;
  }, [accounts]);

  function round4(x) {
    return Math.round((Number(x) || 0) * 10000) / 10000;
  }

  const activeCount = accounts.filter((a) =>
    Object.values(a.balances || {}).some((v) => Math.abs(v) > 0.0001)
  ).length;

  return (
    <div className="space-y-6" data-testid="vault-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div className="min-w-0">
          <div className="label-caps">Müşteri Emanet Kasa</div>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl mt-2 font-light">Emanet</h1>
          <p className="text-neutral-500 text-xs sm:text-sm mt-2">Altın, gümüş, ziynet ve döviz — hepsi tek panelde</p>
        </div>
        <button
          onClick={() => openTxn("deposit")}
          className="btn-primary-gold flex items-center justify-center gap-2 py-3 sm:py-2"
          data-testid="new-deposit-btn"
        >
          <ArrowDownCircle size={14} /> Yeni İşlem
        </button>
      </div>

      {/* Overall totals — çoklu birim */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
        {unitList
          .filter(([u]) => (totals[u] || 0) > 0 || u === "gold_pure_gr")
          .map(([u, info]) => (
            <div key={u} className="card-flat p-3 sm:p-4" data-testid={`total-${u}`}>
              <div className="label-caps flex items-center gap-1.5">
                <span style={{ color: "var(--gold)" }}>{UNIT_ICONS[u] || <Coins size={12} />}</span>
                <span className="truncate">{info.label}</span>
              </div>
              <div
                className="font-mono text-xl sm:text-2xl mt-2 tabular-nums leading-tight"
                style={{ color: "var(--gold)" }}
              >
                {fmt(totals[u] || 0, info.fmt || 2)}
              </div>
            </div>
          ))}
        <div className="card-flat p-3 sm:p-4">
          <div className="label-caps">Aktif Hesap</div>
          <div className="font-mono text-xl sm:text-2xl mt-2 tabular-nums">{activeCount}</div>
        </div>
        <div className="card-flat p-3 sm:p-4">
          <div className="label-caps">Toplam Müşteri</div>
          <div className="font-mono text-xl sm:text-2xl mt-2 tabular-nums">{accounts.length}</div>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 lg:gap-6">
        {/* Accounts list */}
        <div className="xl:col-span-2 card-flat overflow-x-auto">
          {/* Mobile card list */}
          <div className="sm:hidden divide-y divide-white/5">
            {accounts.map((a) => {
              const bal = a.balances || {};
              const nonZero = Object.entries(bal).filter(([, v]) => Math.abs(v) > 0.0001);
              return (
                <button
                  key={a.id}
                  onClick={() => openDetail(a.customer_id)}
                  className="w-full text-left p-4 hover:bg-white/[.02] active:bg-white/[.05] transition"
                  data-testid={`vault-row-${a.customer_id}`}
                >
                  <div className="flex justify-between items-start gap-3">
                    <div className="min-w-0">
                      <div className="font-medium truncate">{a.customer_name}</div>
                      <div className="text-xs text-neutral-500 font-mono truncate">{a.customer_phone || "—"}</div>
                    </div>
                    <div className="text-right shrink-0">
                      {nonZero.length === 0 && <div className="text-neutral-600 text-xs">Bakiye yok</div>}
                      {nonZero.slice(0, 2).map(([u, v]) => (
                        <div key={u} className="font-mono text-xs tabular-nums" style={{ color: "var(--gold)" }}>
                          {fmt(v, units[u]?.fmt || 2)} <span className="text-[10px] text-neutral-500">{units[u]?.label?.split(" ")[0]}</span>
                        </div>
                      ))}
                      {nonZero.length > 2 && (
                        <div className="text-[10px] text-neutral-500">+{nonZero.length - 2} daha</div>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
            {accounts.length === 0 && (
              <div className="text-center py-12 text-neutral-500 text-sm">Kayıt yok</div>
            )}
          </div>

          {/* Desktop table */}
          <table className="w-full text-sm hidden sm:table">
            <thead>
              <tr className="text-neutral-500 label-caps border-b border-white/5">
                <th className="text-left px-4 lg:px-6 py-4">Müşteri</th>
                <th className="text-left px-4 lg:px-6 py-4">Telefon</th>
                <th className="text-left px-4 lg:px-6 py-4">Bakiye Özeti</th>
                <th className="text-right px-4 lg:px-6 py-4 hidden md:table-cell">Güncelleme</th>
              </tr>
            </thead>
            <tbody>
              {accounts.map((a) => {
                const bal = a.balances || {};
                const nonZero = Object.entries(bal).filter(([, v]) => Math.abs(v) > 0.0001);
                return (
                  <tr
                    key={a.id}
                    className="border-b border-white/5 hover:bg-white/[.02] cursor-pointer"
                    onClick={() => openDetail(a.customer_id)}
                    data-testid={`vault-row-${a.customer_id}`}
                  >
                    <td className="px-4 lg:px-6 py-4">{a.customer_name}</td>
                    <td className="px-4 lg:px-6 py-4 font-mono text-neutral-400 text-xs">{a.customer_phone || "—"}</td>
                    <td className="px-4 lg:px-6 py-4">
                      {nonZero.length === 0 && <span className="text-neutral-600 text-xs">—</span>}
                      <div className="flex flex-wrap gap-1.5">
                        {nonZero.map(([u, v]) => (
                          <span
                            key={u}
                            className="inline-flex items-center gap-1 px-2 py-0.5 border border-white/10 font-mono text-xs tabular-nums"
                            style={{ color: "var(--gold)" }}
                          >
                            {fmt(v, units[u]?.fmt || 2)}{" "}
                            <span className="text-neutral-500 text-[10px]">
                              {units[u]?.label?.split(" ")[0]}
                            </span>
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 lg:px-6 py-4 text-right text-neutral-500 text-xs hidden md:table-cell">
                      {new Date(a.updated_at).toLocaleString("tr-TR")}
                    </td>
                  </tr>
                );
              })}
              {accounts.length === 0 && (
                <tr>
                  <td colSpan={4} className="text-center py-12 text-neutral-500">
                    Kayıt yok
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Detail panel */}
        <div className="card-flat p-4 sm:p-6">
          {!selected && <div className="text-neutral-500 text-sm">Detay için bir hesap seçin.</div>}
          {selected && (
            <>
              <div className="label-caps">Seçili Müşteri</div>
              <div className="font-display text-lg sm:text-xl mt-1 mb-3 truncate">{selected.customer?.name}</div>

              {/* Balances grid per unit */}
              <div className="grid grid-cols-2 gap-2 mb-4">
                {Object.entries(selected.vault?.balances || {})
                  .filter(([, v]) => Math.abs(v) > 0.0001)
                  .map(([u, v]) => (
                    <div key={u} className="border border-white/10 p-2" data-testid={`detail-bal-${u}`}>
                      <div className="label-caps flex items-center gap-1 truncate">
                        <span style={{ color: "var(--gold)" }}>{UNIT_ICONS[u]}</span>
                        <span className="truncate">{units[u]?.label || u}</span>
                      </div>
                      <div className="font-mono text-sm mt-1 tabular-nums" style={{ color: "var(--gold)" }}>
                        {fmt(v, units[u]?.fmt || 2)}
                      </div>
                    </div>
                  ))}
                {Object.entries(selected.vault?.balances || {}).filter(([, v]) => Math.abs(v) > 0.0001).length === 0 && (
                  <div className="col-span-2 text-neutral-600 text-xs py-2">Henüz bakiye yok.</div>
                )}
              </div>

              <div className="flex flex-col sm:flex-row gap-2 mb-4">
                <button
                  onClick={() => openTxn("deposit", selected.customer.id, form.unit)}
                  className="btn-gold flex-1 flex items-center justify-center gap-1 py-3 sm:py-2"
                  data-testid="deposit-btn"
                >
                  <ArrowDownCircle size={12} /> Yatır
                </button>
                <button
                  onClick={() => openTxn("withdraw", selected.customer.id, form.unit)}
                  className="btn-gold flex-1 flex items-center justify-center gap-1 py-3 sm:py-2"
                  data-testid="withdraw-btn"
                >
                  <ArrowUpCircle size={12} /> Çek
                </button>
              </div>

              {/* Second-by-second timeline */}
              <div className="max-h-72 overflow-auto divide-y divide-white/5 border-t border-white/5" data-testid="txn-timeline">
                {selected.txns.map((t) => {
                  const info = units[t.unit] || DEFAULT_UNITS[t.unit] || { label: t.unit || "gr", fmt: 4 };
                  const q = Number(t.quantity ?? t.gram ?? 0);
                  const created = new Date(t.created_at);
                  return (
                    <div key={t.id} className="py-3">
                      <div className="flex justify-between text-sm gap-2">
                        <span className={t.type === "deposit" ? "text-emerald-400" : "text-red-400"}>
                          {t.type === "deposit" ? "+" : "−"}
                          {fmt(q, info.fmt || 2)} <span className="text-xs text-neutral-500">{info.label}</span>
                        </span>
                        <span
                          className="font-mono text-[10px] sm:text-xs text-neutral-500 shrink-0 tabular-nums"
                          title={created.toISOString()}
                        >
                          {created.toLocaleDateString("tr-TR")}{" "}
                          {created.toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
                        </span>
                      </div>
                      <div className="label-caps mt-1">
                        Bakiye {fmt(t.balance_after, info.fmt || 2)} {info.label.split(" ")[0]}
                        {t.note ? <span className="text-neutral-500"> · {t.note}</span> : null}
                      </div>
                    </div>
                  );
                })}
                {selected.txns.length === 0 && (
                  <div className="py-4 text-sm text-neutral-500">Hareket yok</div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Txn modal */}
      {txnOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-black/70 backdrop-blur-sm" data-testid="txn-modal">
          <div className="w-full max-w-md card-flat p-5 sm:p-8 max-h-[92vh] overflow-y-auto" style={{ background: "var(--surface)" }}>
            <div className="flex justify-between items-start mb-5 sm:mb-6">
              <div className="min-w-0">
                <div className="label-caps">Emanet</div>
                <div className="font-display text-xl sm:text-2xl mt-1">
                  {form.type === "deposit" ? "Yatır" : "Çek"} · {units[form.unit]?.label || form.unit}
                </div>
              </div>
              <button onClick={() => setTxnOpen(false)} className="text-neutral-400 hover:text-white shrink-0" aria-label="Kapat">
                <X size={18} />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="label-caps block mb-2">Müşteri</label>
                <div className="flex gap-2">
                  <select
                    value={form.customer_id}
                    onChange={(e) => setForm({ ...form, customer_id: e.target.value })}
                    className="flex-1 min-w-0 border border-white/10 px-3 py-3 sm:py-2 text-sm"
                    style={{ background: "var(--surface-2)" }}
                    data-testid="txn-customer"
                  >
                    <option value="">Seçin...</option>
                    {customers.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                  <QuickCustomerAdd
                    testid="vault-quick-customer"
                    onCreated={(c) => {
                      setCustomers((prev) => [...prev, c]);
                      setForm((f) => ({ ...f, customer_id: c.id }));
                    }}
                  />
                </div>
              </div>

              <div>
                <label className="label-caps block mb-2">Birim</label>
                <select
                  value={form.unit}
                  onChange={(e) => {
                    const u = e.target.value;
                    setForm((f) => ({ ...f, unit: u, purity: units[u]?.purity || 0.995 }));
                  }}
                  className="w-full border border-white/10 px-3 py-3 sm:py-2 text-sm"
                  style={{ background: "var(--surface-2)" }}
                  data-testid="txn-unit"
                >
                  {unitList.map(([u, info]) => (
                    <option key={u} value={u}>
                      {info.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className={`grid ${showPurity ? "grid-cols-2" : "grid-cols-1"} gap-3`}>
                <div>
                  <label className="label-caps block mb-2">Miktar</label>
                  <input
                    type="number"
                    step={unitInfo.kind === "coin" ? "1" : "0.0001"}
                    value={form.quantity}
                    onChange={(e) => setForm({ ...form, quantity: e.target.value })}
                    className="w-full bg-transparent border border-white/10 px-3 py-3 sm:py-2 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-[--gold]"
                    data-testid="txn-quantity"
                  />
                </div>
                {showPurity && (
                  <div>
                    <label className="label-caps block mb-2">Ayar</label>
                    <select
                      value={form.purity}
                      onChange={(e) => setForm({ ...form, purity: e.target.value })}
                      className="w-full border border-white/10 px-3 py-3 sm:py-2 text-sm"
                      style={{ background: "var(--surface-2)" }}
                      data-testid="txn-purity"
                    >
                      <option value="0.995">24 Ayar (995)</option>
                      <option value="0.916">22 Ayar (916)</option>
                      <option value="0.750">18 Ayar (750)</option>
                      <option value="0.585">14 Ayar (585)</option>
                    </select>
                  </div>
                )}
              </div>

              <div>
                <label className="label-caps block mb-2">Not</label>
                <input
                  value={form.note}
                  onChange={(e) => setForm({ ...form, note: e.target.value })}
                  className="w-full bg-transparent border border-white/10 px-3 py-3 sm:py-2 text-sm focus:outline-none"
                  placeholder="Örn. bilezik emanet, düğün için"
                />
              </div>
            </div>
            <button
              onClick={submit}
              className="btn-primary-gold w-full mt-6 py-3"
              data-testid="txn-submit"
            >
              Onayla
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
